import { cx, formatPhone } from "shared/utils";
import {
  ApplicantType,
  ApplicationStatus,
  ApplicationFeeStatus
} from "shared/types/application";
import {
  Phone,
  Mail01,
  Archive,
  FlipBackward,
  Check,
  Minus
} from "untitledui-js-base";
import { ApplicationStatusBadge } from "shared/app/ApplicationStatusBadge";
import moment from "moment";
import { ContextMenuPopover } from "shared/components/ContextMenuPopover";
import { Button, ButtonSize } from "shared/components";
import { useContext, useEffect } from "react";
import { PrintAllContext } from "views/applications-id-report/providers/PrintProvider";
import { useLocation, useNavigate } from "react-router-dom";
import { Redirect } from "shared/components/Redirect";
import { VerificationStatus } from "shared/types/verification";
import { getFullName } from "shared/utils/get-full-name";
import { Show } from "shared/components";
import { formatTitleFirstWord } from "../../../utils/formatTitle";
import { formatToCurrency } from "../../../utils/currency";
import { useAuthContext } from "providers/Auth";
import { Applicant } from "types/application";

interface ApplicantCardSliceProps {
  applicant?: Applicant;
  onCancelClick?: () => void;
  onRefundClick?: () => void;
  applicationStatus: ApplicationStatus;
  verificationIds: number[];
}

export const ApplicantCardSlice = ({
  applicant,
  onCancelClick,
  onRefundClick,
  applicationStatus,
  verificationIds
}: ApplicantCardSliceProps) => {
  const { setDocumentPrintingState, setDocuments } =
    useContext(PrintAllContext);
  const location = useLocation();
  const navigate = useNavigate();
  const { type, permissions } = useAuthContext();

  useEffect(() => {
    if (location.pathname.includes("/print")) {
      setDocuments({});
      setDocumentPrintingState(
        verificationIds.map((id) => {
          return { status: "loading", reportId: id };
        })
      );
    }
  }, []);

  const hasPendingProcessingVerifications = applicant?.verifications.some(
    (verification) => verification.status === VerificationStatus.Processing
  );

  const status =
    applicationStatus === ApplicationStatus.Canceled
      ? ApplicationStatus.Canceled
      : hasPendingProcessingVerifications &&
          applicationStatus === ApplicationStatus.Submitted
        ? ApplicationStatus.Processing
        : applicant?.application_status;

  return (
    <div className="rounded-lg border border-alpha-100 bg-white p-3 md:p-6 shadow-xs">
      {applicant ? (
        <>
          <div className="mb-4 flex justify-between gap-2 border-b border-alpha-40 pb-4">
            <div className="flex gap-4 w-full md:flex-nowrap flex-wrap">
              <div>
                <h1 className="text-xl font-bold text-content-1000">
                  {getFullName({
                    firstName: applicant.first_name,
                    lastName: applicant.last_name,
                    middleName: applicant.middle_name,
                    short: true
                  })}
                </h1>
                <p className="text-sm capitalize text-content-400">
                  {applicant.role === ApplicantType.CoSigner
                    ? "Guarantor"
                    : applicant.role}
                </p>
              </div>

              <div>
                <ApplicationStatusBadge
                  refunded={applicant.refunded}
                  status={status}
                  type="applicant"
                />
              </div>
              <div className="flex gap-4 items-center ml-auto">
                <div className="print:hidden">
                  <ContextMenuPopover
                    borderLess={false}
                    actions={[
                      {
                        title: (
                          <span className="flex gap-2 items-center">
                            <Archive size="18" className="stroke-content-200" />
                            Cancel application
                          </span>
                        ),
                        tooltip:
                          applicationStatus === ApplicationStatus.Canceled
                            ? "Cannot cancel a canceled application"
                            : type.isAdmin ||
                                type.isOwner ||
                                permissions.canCancelApplications
                              ? "You can't cancel application once the decisions have been made"
                              : "You don't have permissions to cancel this application",
                        disabled:
                          (
                            [
                              ApplicationStatus.Canceled,
                              ApplicationStatus.Approved,
                              ApplicationStatus.Rejected,
                              ApplicationStatus.ConditionalApproved
                            ] as string[]
                          ).includes(applicationStatus) ||
                          (!type.isAdmin &&
                            !type.isOwner &&
                            !permissions.canCancelApplications),
                        onClick: () => {
                          if (
                            !(
                              [
                                ApplicationStatus.Canceled,
                                ApplicationStatus.Approved,
                                ApplicationStatus.Rejected,
                                ApplicationStatus.ConditionalApproved
                              ] as string[]
                            ).includes(applicationStatus)
                          )
                            onCancelClick && onCancelClick();
                        }
                      },
                      {
                        title: (
                          <span className="flex gap-2 items-center">
                            <FlipBackward
                              size="18"
                              className="stroke-content-200"
                            />
                            Refund application
                          </span>
                        ),
                        disabled:
                          applicant.application_fee_status !==
                            ApplicationFeeStatus.Paid ||
                          applicant.application_status !==
                            ApplicationStatus.Submitted,
                        tooltip:
                          applicant.application_status ===
                          ApplicationStatus.Submitted
                            ? applicant.application_fee_status ===
                              ApplicationFeeStatus.Refunded
                              ? "Refund has already been requested for this application"
                              : applicant.application_fee_status ===
                                  ApplicationFeeStatus.Paid
                                ? ""
                                : `You can request a refund if the ${
                                    applicant.role === ApplicantType.Applicant
                                      ? "applicant"
                                      : applicant.role ===
                                          ApplicantType.CoSigner
                                        ? "guarantor"
                                        : ""
                                  } has paid the application fee`
                            : "Refund has already been requested for this application",
                        onClick: () => onRefundClick && onRefundClick()
                      }
                    ]}
                  />
                </div>

                {location.pathname.includes("/print") ? null : (
                  <Button
                    className="print:hidden md:flex hidden min-w-[125px]"
                    onClick={() => {
                      navigate(
                        `/applications/${applicant.application_id}/reports/print?applicantId=${applicant.id}`,
                        {
                          state: {
                            refresh: true
                          }
                        }
                      );
                    }}
                    size={ButtonSize.SM}
                  >
                    Print application
                  </Button>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-end justify-between">
            <div className="flex items-end gap-8 md:flex-nowrap flex-wrap">
              <div className="flex flex-col gap-2">
                <div className="flex items-center gap-2 text-sm font-medium text-content-1000">
                  <Phone size="16" className="stroke-content-100" />
                  {formatPhone(applicant.phone || "") || "--"}
                </div>

                <div className="flex items-center gap-2 text-sm font-medium text-content-1000">
                  <Mail01 size="16" className="stroke-content-100" />
                  {applicant.email}
                </div>
              </div>

              <Show if={applicant.application_started_at}>
                <div className="flex flex-col gap-2">
                  <p className="text-xs text-content-400 font-normal">
                    Start date
                  </p>
                  <p className="text-sm font-normal text-content-1000">
                    {moment(applicant.application_started_at).format(
                      "MMM DD, YYYY [at] h:mm A"
                    )}
                  </p>
                </div>
              </Show>

              <Show if={applicant.application_finished_at}>
                <div className="flex flex-col gap-2">
                  <p className="text-xs text-content-400 font-normal">
                    Submission date
                  </p>
                  <p className="text-sm font-normal text-content-1000">
                    {moment(applicant.application_finished_at).format(
                      "MMM DD, YYYY [at] h:mm A"
                    )}
                  </p>
                </div>
              </Show>
            </div>
            <div className="flex flex-col gap-2 items-end">
              <p className="text-xs text-content-400 font-normal">
                Application fee
              </p>
              <p className="text-sm font-normal text-content-1000">
                {applicant.application_fee_status !==
                ApplicationFeeStatus.NoApplicationFee ? (
                  <div className="flex gap-2 items-center">
                    {
                      {
                        [ApplicationFeeStatus.Paid]: (
                          <Check size="20" className="stroke-success-600" />
                        ),
                        [ApplicationFeeStatus.Refunded]: (
                          <FlipBackward
                            size="20"
                            className="stroke-warning-500"
                          />
                        ),
                        [ApplicationFeeStatus.Unpaid]: (
                          <Minus className="stroke-content-200" />
                        )
                      }[
                        applicant.application_fee_status ||
                          ApplicationFeeStatus.Unpaid
                      ]
                    }
                    <p
                      className={cx(
                        "font-medium",
                        applicant.application_fee_status ===
                          ApplicationFeeStatus.Unpaid
                          ? "text-content-200"
                          : "text-content-1000"
                      )}
                    >
                      {formatTitleFirstWord(
                        applicant.application_fee_status ||
                          ApplicationFeeStatus.Unpaid
                      )}
                    </p>
                    <p
                      className={
                        applicant.application_fee_status ===
                        ApplicationFeeStatus.Unpaid
                          ? "text-content-200"
                          : "text-content-300"
                      }
                    >
                      (
                      {applicant.application_fee_status ===
                      ApplicationFeeStatus.Unpaid
                        ? applicant.application_fee &&
                          formatToCurrency(applicant.application_fee)
                        : applicant.paid_application_fee &&
                          formatToCurrency(applicant.paid_application_fee)}
                      )
                    </p>
                  </div>
                ) : (
                  "-"
                )}
              </p>
            </div>
          </div>
        </>
      ) : (
        <Redirect href="/applications" />
      )}
    </div>
  );
};
